import {ActionIcon, TextInput} from '@mantine/core';
import {Table} from 'src/design/atoms/Table';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconPlus, IconTrash} from '@tabler/icons-react';
import React from 'react';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';
import type {ContactResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {IFormDataV2} from 'src/types/FormV2';

/**
 * Repeating name/title/email/phone rows for a Client or Site's contacts.
 *
 * Contacts are not part of the ProactForm metadata engine (they're a
 * variable-length nested collection, not a scalar field) but they live on
 * the same react-hook-form instance as the surrounding ProactForm — this
 * must render inside a `<ProactForm>` so `useFormContext` resolves to the
 * form's `control`. `contacts` is seeded via the parent's `resetForm(entity)`
 * call (entity-shaped resets pass extraneous keys through untouched) and
 * read back at submit with `methods.getValues('contacts')`, since it's
 * outside the metadata field set `getFieldValues()` filters to.
 */
export const ContactsEditor: React.FC = () => {
  const {control} = useFormContext<IFormDataV2>();
  const {fields, append, remove} = useFieldArray({control, name: 'contacts'});

  return (
    <SectionCard title="Contacts">
      <Stack p="md" gap="sm">
        {fields.length === 0 && (
          <Text size="sm" c="dimmed">
            No contacts added yet.
          </Text>
        )}
        {fields.map((field, index) => (
          <Grid key={field.id} align="flex-end" gutter="sm">
            <Grid.Col span={{base: 12, sm: 3}}>
              <Controller
                name={`contacts.${index}.name`}
                control={control}
                render={({field: f}) => (
                  <TextInput
                    {...f}
                    value={f.value ?? ''}
                    label={index === 0 ? 'Name' : undefined}
                    placeholder="Jane Doe"
                    data-testid={`contact-name-${index}`}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{base: 12, sm: 3}}>
              <Controller
                name={`contacts.${index}.title`}
                control={control}
                render={({field: f}) => (
                  <TextInput
                    {...f}
                    value={f.value ?? ''}
                    label={index === 0 ? 'Title' : undefined}
                    placeholder="Facilities Manager"
                    data-testid={`contact-title-${index}`}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{base: 12, sm: 3}}>
              <Controller
                name={`contacts.${index}.email`}
                control={control}
                render={({field: f}) => (
                  <TextInput
                    {...f}
                    value={f.value ?? ''}
                    label={index === 0 ? 'Email' : undefined}
                    placeholder="jane.doe@example.com"
                    data-testid={`contact-email-${index}`}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{base: 10, sm: 2}}>
              <Controller
                name={`contacts.${index}.phone`}
                control={control}
                render={({field: f}) => (
                  <TextInput
                    {...f}
                    value={f.value ?? ''}
                    label={index === 0 ? 'Phone' : undefined}
                    placeholder="(555) 555-5555"
                    data-testid={`contact-phone-${index}`}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={{base: 2, sm: 1}}>
              <ActionIcon
                color="red"
                variant="subtle"
                aria-label="Remove contact"
                onClick={() => remove(index)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Grid.Col>
          </Grid>
        ))}
        <Group>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={() => append({name: '', title: '', email: '', phone: ''})}>
            Add Contact
          </Button>
        </Group>
      </Stack>
    </SectionCard>
  );
};

export interface ContactsViewTableProps {
  contacts: ContactResponse[] | undefined;
}

/** Read-only contacts display for View pages. */
export const ContactsViewTable: React.FC<ContactsViewTableProps> = ({
  contacts,
}) => {
  const rows = contacts ?? [];

  return (
    <SectionCard title="Contacts">
      <Stack p="md" gap="sm">
        {rows.length === 0 ? (
          <Text size="sm" c="dimmed">
            No contacts have been added.
          </Text>
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Phone</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(contact => (
                <Table.Tr key={contact.id}>
                  <Table.Td>{contact.name}</Table.Td>
                  <Table.Td>{contact.title}</Table.Td>
                  <Table.Td>{contact.email}</Table.Td>
                  <Table.Td>{contact.phone}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </SectionCard>
  );
};
